import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { ConfirmModal } from '@apply/ui';
import { useSelector } from 'react-redux';
import { InformModal } from '../../common/SharedComponents';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { BfiCustomStore } from '../../common/SharedComponents';
import LinkLoanTable from '../../loans/tables/LinkLoanTable';
import debounce from 'lodash.debounce';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { CommonLoanSummary } from '../../loans/common/LoanComponents';
import { LoanType } from '@apply/constants';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { CommonLoanSummaryInvokingFeatureType } from '../common/constants/CommonLoanSummaryInvokingFeatureType';

const LinkLoanWithBClientModal = () => {
    const { isOpen, bClientId } = useSelector((state) => state.loan.linkLoanWithBClientModal);
    const { dispatch } = store;
    const loanApi = new LoanApi.LoanApi(applicationInsights);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
        formRow: { marginBottom: '0' },
    };

    const [searchText, set_searchText] = useState(null);
    const [loan, set_loan] = useState(null);

    const [informModalOpen, set_informModalOpen] = useState(false);
    const [informModalMessage, set_informModalMessage] = useState(null);

    const [openConfirmModal, set_openConfirmModal] = useState(false);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Saving...');

    const debouncedSearch = debounce(dispatch, 500);
    const gridStore = new BfiCustomStore();
    const refreshEvents = new RefreshEvents();

    const _handleClose = () =>
        dispatch(
            loanActions.setLinkLoanWithBClientModal({
                linkLoanWithBClientModal: { isOpen: false },
            })
        );

    const loadLoanInfo = async (id) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await loanApi.getLoanSummary(id);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }
        const loan = response.result;
        set_loan(loan);
        set_loadingOpen(false);
    };

    const onConfirm = async () => {
        set_openConfirmModal(false);
        await linkLoan();
    };

    const onCancel = () => {
        set_openConfirmModal(false);
    };

    const linkLoan = async () => {
        if (!loan) {
            return;
        }

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const response = await loanApi.linkLoanToBClient(loan.id, bClientId);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }
            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan updated successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    useEffect(() => {
        async function bClientIdChanged() {
            set_searchText(null);
            set_loan(null);
            set_loadingOpen(false);
        }

        bClientIdChanged();
    }, [isOpen, bClientId]);

    const _onSearchChange = (event) => {
        debouncedSearch(set_searchText(event.target.value));
    };

    const onLoanSelect = async (loan) => {
        if (!loan) {
            set_informModalMessage('No Loan selected!');
            set_informModalOpen(true);
            return;
        }

        if (loan.brokerAgreementId === bClientId) {
            set_informModalMessage('The selected loan is already associated with this B-Client!');
            set_informModalOpen(true);
            return;
        }

        await loadLoanInfo(loan.id);
    };

    if (!isOpen || !bClientId) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '1100px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>Link Loan to B-Client</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>

                                {!loan && (
                                    <div
                                        className='main-content'
                                        style={{
                                            padding: 0,
                                            backgroundColor: '#f4f4f4',
                                        }}
                                    >
                                        <div className='input-has-icon main-search'>
                                            <input
                                                type='search'
                                                placeholder='Search on BFI Number, Loan Number, City, State, Borrower or User...'
                                                defaultValue={searchText}
                                                onChange={(event) => _onSearchChange(event)}
                                            />
                                        </div>
                                        {!searchText && (
                                            <div
                                                style={{
                                                    padding: '10px',
                                                    textAlign: 'center',
                                                    color: 'darkblue',
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faInfoCircle} />
                                                &nbsp;&nbsp;Enter some text into the search box above...
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!loan && (
                                    <div
                                        className='scrollable-content'
                                        style={{
                                            padding: '1rem',
                                            height: '380px',
                                        }}
                                    >
                                        {searchText && (
                                            <LinkLoanTable
                                                gridStore={gridStore}
                                                searchText={searchText}
                                                onSelect={onLoanSelect}
                                            />
                                        )}
                                    </div>
                                )}

                                {loan && (
                                    <div style={{ padding: '1rem' }}>
                                        <div
                                            style={{
                                                padding: '10px',
                                                textAlign: 'center',
                                                color: 'darkred',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {loan.loanTypeId === LoanType.MasterNote && (
                                                <>
                                                    &nbsp;&nbsp;Clicking the 'Link' button will remove the selected Loan's
                                                    Borrower and Broker (or B-Client) and replace with those associated with the
                                                    current B-Client. <br />
                                                    <br />
                                                    <b>All Sub-Notes linked to this MasterNote will also be updated</b>
                                                    <br />
                                                    <br /> Use with caution.
                                                </>
                                            )}
                                            {loan.loanTypeId !== LoanType.MasterNote && (
                                                <>
                                                    &nbsp;&nbsp;Clicking the 'Link' button will remove the selected Loan's
                                                    Borrower and Broker (or B-Client) and replace with those associated with the
                                                    current B-Client. <br />
                                                    <br />
                                                    Use with caution.
                                                </>
                                            )}
                                        </div>
                                        <CommonLoanSummary
                                            loan={loan}
                                            invokingFeature={CommonLoanSummaryInvokingFeatureType.LinkLoanWithBClient}
                                        />
                                        <div style={{ textAlign: 'right' }}>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={(e) => set_loan(null)}
                                            >
                                                Back to Search
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div
                                    className='standard-form no-search'
                                    style={{ padding: '0', margin: '0' }}
                                >
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            type='button'
                                            className='button'
                                            data-dismiss='modal'
                                            onClick={(e) => set_openConfirmModal(true)}
                                            disabled={!loan}
                                        >
                                            Link
                                        </button>
                                        &nbsp;
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <InformModal
                isOpen={informModalOpen}
                text={informModalMessage}
                confirmCallback={(e) => set_informModalOpen(false)}
            />
            <ConfirmModal
                isOpen={openConfirmModal}
                text={'Are you sure you wish to link this loan to the current B-Client?'}
                confirmCallback={onConfirm}
                cancelCallback={onCancel}
            />
        </>
    );
};

export default LinkLoanWithBClientModal;
